import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { authService } from '../../lib/auth-service';
import { useRoleAwareLayout } from '../../hooks/responsive/useResponsive';
import { useSidebarBadges } from '../../hooks/useSidebarBadges';
import CompactViewSwitcher from './CompactViewSwitcher';
import { 
  Home, 
  Calendar, 
  Lightbulb, 
  Landmark, 
  ClipboardList, 
  ShoppingCart, 
  MessageSquare, 
  CheckSquare, 
  ArrowLeft,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  SquareCheckBig,
  Search,
  BookOpen,
  Users,
  User,
  Settings,
  GraduationCap,
  Brain,
  Luggage
} from 'lucide-react';
 
const TABS = [
  {
    key: 'home',
    label: 'Dashboard',
    path: null,
    icon: <Home className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },

  {
    key: 'activities',
    label: 'Activities',
    path: '/activities',
    icon: <Lightbulb className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'family',
    label: 'Family Outings',
    path: '/family',
    icon: <Calendar className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'shopping',
    label: 'Shopping List',
    path: '/shopping',
    icon: <ShoppingCart className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'packing',
    label: 'Packing Lists',
    path: '/packing',
    icon: <Luggage className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'reminders',
    label: 'Send Reminder',
    path: '/messages',
    icon: <MessageSquare className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'tasks',
    label: 'Tasks',
    path: '/tasks',
    icon: <SquareCheckBig className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'income-hub',
    label: 'Income Hub',
    path: '/income-hub',
    icon: <Landmark className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },

  {
    key: 'recs-ideas',
    label: 'Recs & Ideas',
    path: '/recs-ideas',
    icon: <Lightbulb className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'summer-explorer',
    label: 'College Prep',
    path: '/summer-explorer',
    icon: <Search className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    isBeta: true
  },
  {
    key: 'college-journey',
    label: 'College Journey',
    path: '/college',
    icon: <GraduationCap className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    isBeta: true
  },
  {
    key: 'reading-adventure',
    label: 'Reading Challenge',
    path: '/reading',
    icon: <BookOpen className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both']
  },
  {
    key: 'ai-intelligence',
    label: 'AI Intelligence Feed',
    path: '/ai-intelligence',
    icon: <Brain className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    isBeta: true
  },
  {
    key: 'intelligence-dashboard',
    label: 'Intelligence Dashboard',
    path: '/intelligence-dashboard',
    icon: <Brain className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    isBeta: true
  },

];

const NAV_GROUPS = [
  {
    title: 'Main',
    items: ['home', 'tasks', 'shopping', 'packing', 'family', 'income-hub', 'recs-ideas', 'reading-adventure', 'summer-explorer', 'college-journey', 'ai-intelligence', 'intelligence-dashboard']
  }
];

export default function SideNav() {
  const { currentUser, setCurrentUser, family, familyUsers, switchUser } = useFamilyContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on a dashboard route
  const isDashboardRoute = location.pathname === '/dashboard' ||
                          location.pathname === '/parent-dashboard' ||
                          location.pathname === '/';

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Check for saved user preference
    const savedPreference = localStorage.getItem('sidebar-collapsed-preference');
    const savedRoute = localStorage.getItem('sidebar-last-route');

    // If user has manually toggled on the same route type, respect their preference
    if (savedPreference !== null && savedRoute === (isDashboardRoute ? 'dashboard' : 'other')) {
      return savedPreference === 'true';
    }

    // Otherwise, use route-based default
    // For tablets and smaller screens, always start collapsed
    if (typeof window !== 'undefined' && window.innerWidth < 1200) {
      return true;
    }

    // Desktop: expanded on dashboard, collapsed elsewhere
    return !isDashboardRoute;
  });
  const [manualOverride, setManualOverride] = useState(false);  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showKeyboardHint, setShowKeyboardHint] = useState(false);
  // Use unified responsive system
  const { isMobile, isTablet, isDesktop } = useRoleAwareLayout();

  // Get badge counts
  const { badges } = useSidebarBadges();

  // Use unified responsive system instead of separate getScreenSize
  const screenSize = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  // Dispatch collapsed state to SidebarLayout when it changes
  useEffect(() => {
    // Small delay to ensure SidebarLayout is ready to receive the event
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('sidebarToggle', {
        detail: { collapsed: isCollapsed }
      }));
    }, 0);

    return () => clearTimeout(timer);
  }, [isCollapsed]); // Run whenever isCollapsed changes

  // Handle route changes - update default state when navigating between dashboard and other pages
  useEffect(() => {
    // Only update if user hasn't manually overridden
    if (!manualOverride && isDesktop) {
      const newIsDashboardRoute = location.pathname === '/dashboard' ||
                                  location.pathname === '/parent-dashboard' ||
                                  location.pathname === '/';

      // Clear saved preference if switching route types
      const savedRoute = localStorage.getItem('sidebar-last-route');
      const currentRouteType = newIsDashboardRoute ? 'dashboard' : 'other';

      if (savedRoute !== currentRouteType) {
        localStorage.removeItem('sidebar-collapsed-preference');
        localStorage.setItem('sidebar-last-route', currentRouteType);
        // Apply new default based on route
        setIsCollapsed(!newIsDashboardRoute);
        setManualOverride(false);
      }
    }
  }, [location.pathname, isDesktop, manualOverride]);

  // Initialize collapsed state based on screen size
  useEffect(() => {
    if (isTablet) {
      setIsCollapsed(true);
      setManualOverride(false); // Reset manual override on tablet
    }
  }, [isTablet]);

  const isParent = currentUser?.isParent || currentUser?.originalParent;
  const isViewingAsKid = !currentUser?.isParent && currentUser?.originalParent;
  const role = isViewingAsKid ? 'kid' : (isParent ? 'parent' : 'kid');

  // Set dynamic paths based on role - using modular architecture
  const tabsWithDynamicPaths = TABS.map(tab => {
    if (tab.key === 'home') {
      // Use unified dashboard
      const homePath = '/dashboard';
      
      return {
        ...tab,
        path: homePath
      };
    }
    
    if (tab.key === 'family') {
      // Use the unified events page with tabs
      return {
        ...tab,
        path: '/family'
      };
    }
    
    if (tab.key === 'reading-adventure') {
      // Parents see admin view, kids see their personal view
      return {
        ...tab,
        path: isParent ? '/reading/admin' : '/reading'
      };
    }
    
    if (tab.key === 'reminders') {
      return {
        ...tab,
        path: tab.path // Keep existing messages path
      };
    }
    
    return tab;
  });

  const visibleTabs = tabsWithDynamicPaths.filter(tab => {
    // First check if the tab is for this role
    if (!tab.roles.includes(role)) return false;
    
    // Check if tab has a restriction (like Malcolm-only features)
    if (tab.restricted) {
      return currentUser?.name === tab.restricted || currentUser?.id === 'parent2' || currentUser?.id === 'parent1';
    }
    
    return true;
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        if (screenSize !== 'mobile') {
          toggleSidebar();
        }
      }
      if (e.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };
    
    // Handle custom sidebar toggle events
    const handleSidebarToggle = (e) => {
      if (e.detail && typeof e.detail.collapsed === 'boolean') {
        setIsCollapsed(e.detail.collapsed);
        setManualOverride(e.detail.collapsed ? 'collapsed' : 'expanded');
      }
    };
    
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, [isMobileOpen, screenSize]);

  // Handle responsive state changes
  useEffect(() => {
    // Close mobile menu when resizing to larger screens
    if (!isMobile && isMobileOpen) {
      setIsMobileOpen(false);
    }

    // Auto-collapse on tablet
    if (isTablet) {
      setIsCollapsed(true);
      setManualOverride(false); // Reset manual override when switching to tablet
    } else if (isDesktop && !manualOverride) {
      // On desktop, apply route-based default only if user hasn't manually overridden
      const savedPreference = localStorage.getItem('sidebar-collapsed-preference');
      const savedRoute = localStorage.getItem('sidebar-last-route');
      const currentRouteType = isDashboardRoute ? 'dashboard' : 'other';

      if (savedPreference !== null && savedRoute === currentRouteType) {
        // Restore user's preference for this route type
        setIsCollapsed(savedPreference === 'true');
      } else {
        // Apply route-based default
        setIsCollapsed(!isDashboardRoute);
      }
    }
  }, [isMobile, isTablet, isDesktop, isMobileOpen, isDashboardRoute, manualOverride]);

  // Show keyboard hint on first load (desktop/tablet only)
  useEffect(() => {
    if (screenSize !== 'mobile') {
      const timer = setTimeout(() => {
        setShowKeyboardHint(true);
        setTimeout(() => setShowKeyboardHint(false), 3000);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [screenSize]);

  const toggleSidebar = () => {
    setIsTransitioning(true);
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    // Save user's manual preference
    const currentRouteType = isDashboardRoute ? 'dashboard' : 'other';
    localStorage.setItem('sidebar-collapsed-preference', newCollapsedState.toString());
    localStorage.setItem('sidebar-last-route', currentRouteType);
    setManualOverride(true); // Mark that user has manually overridden

    // Dispatch event for layout component
    window.dispatchEvent(new CustomEvent('sidebarToggle', {
      detail: { collapsed: newCollapsedState }
    }));

    setTimeout(() => setIsTransitioning(false), 300);

    // Show keyboard hint
    setShowKeyboardHint(true);
    setTimeout(() => setShowKeyboardHint(false), 2000);
  };

  const returnToParentView = async () => {
    const user = await authService.getUser();
    if (!user) return;

    localStorage.removeItem(`family-data-${user.id}`);
    localStorage.removeItem(`family-data-timestamp-${user.id}`);

    const loggedInParent = family?.parents?.find(p => p.authUserId === user?.id);
    if (loggedInParent) {
      setCurrentUser({
        ...loggedInParent,
        isParent: true,
        originalParent: false
      });
      navigate('/parent-dashboard');
    }
  };

  const handleNavClick = (item, e) => {
    // Visual feedback - check if element exists
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.add('sidebar-nav-clicked');
      setTimeout(() => {
        if (e.currentTarget && e.currentTarget.classList) {
          e.currentTarget.classList.remove('sidebar-nav-clicked');
        }
      }, 300);
    }

    // Auto-close mobile menu
    if (window.innerWidth <= 1200) {
      setTimeout(() => {
        setIsMobileOpen(false);
      }, 150);
    }
  };

  const getNavGroups = () => {
    return NAV_GROUPS.map(group => ({
      ...group,
      items: group.items
        .map(key => visibleTabs.find(tab => tab.key === key))
        .filter(Boolean)
    })).filter(group => group.items.length > 0);
  };

  const getUserInitials = () => {
    if (!currentUser?.name) return 'U';
    return currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Render badge for a specific tab
  const renderBadge = (tabKey, count, colorClass) => {
    if (!count || count === 0) return null;

    return (
      <span className={`${
        isCollapsed
          ? 'absolute top-0 left-6 w-4 h-4 text-white text-[10px] font-bold rounded-full flex items-center justify-center'
          : 'absolute -top-0.5 left-6 w-4 h-4 text-white text-[10px] font-bold rounded-full flex items-center justify-center'
      } ${colorClass} transition-all duration-200`}>
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {screenSize === 'mobile' && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className="fixed top-4 left-4 z-70 p-2 bg-white rounded-lg shadow-md border border-app-subtle hover:bg-stone-50 transition-colors"
          aria-label="Open navigation menu"
        >
          <Menu className="w-5 h-5 text-app-primary" />
        </button>
      )}

      {/* Sidebar Overlay for Mobile and Tablet (when expanded) */}
      {(screenSize === 'mobile' || (screenSize === 'tablet' && !isCollapsed)) && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-all duration-300 ${
            (screenSize === 'mobile' ? isMobileOpen : !isCollapsed) ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          onClick={() => {
            if (screenSize === 'mobile') {
              setIsMobileOpen(false);
            } else {
              setIsCollapsed(true);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          h-screen bg-white border-r border-app-subtle 60 
          transition-all duration-300 ease-in-out shadow-lg
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${screenSize === 'mobile' 
            ? `fixed top-0 left-0 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`
            : screenSize === 'tablet'
              ? `fixed top-0 left-0 ${isCollapsed ? 'translate-x-0' : 'translate-x-0'}`
              : 'fixed top-0 left-0' // Desktop - Fixed positioning
          }
          ${isTransitioning ? 'pointer-events-none' : ''}
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="p-5 border-b border-app-subtle flex items-center justify-between min-h-[80px]">
          <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
            <img 
              src="/icons/icon-96-transparent.png" 
              alt="Paper Sedge" 
              className="w-9 h-9 rounded-xl shadow-sm flex-shrink-0"
            />
            <span 
              className={`font-light text-xl text-app-primary transition-all duration-200 ${
                isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
              }`}
            >
              Paper Sedge
            </span>
          </div>
          
          {/* Desktop Collapse Button */}
          {screenSize === 'desktop' && (
            <button
              onClick={toggleSidebar}
              className={`p-2 hover:bg-stone-100 rounded-lg transition-colors ${
                isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
              }`}
              title={`${isCollapsed ? 'Expand' : 'Collapse'} sidebar (⌘B)`}
            >
              <ChevronLeft className="w-4 h-4 text-app-tertiary" />
            </button>
          )}

          {/* Tablet/Mobile Close Button */}
          {(screenSize === 'mobile' || (screenSize === 'tablet' && !isCollapsed)) && (
            <button
              onClick={() => {
                if (screenSize === 'mobile') {
                  setIsMobileOpen(false);
                } else {
                  setIsCollapsed(true);
                }
              }}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-app-tertiary" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {getNavGroups().map(group => (
            <div key={group.title} className="mb-8">
              <h3 
                className={`text-xs font-regular text-app-subtle uppercase tracking-wide mb-3 transition-all duration-200 ${
                  isCollapsed ? 'opacity-0 h-0 mb-0 overflow-hidden' : 'opacity-100'
                }`}
              >
                {group.title}
              </h3>
              <ul className={`${isCollapsed ? 'space-y-2' : 'space-y-5'}`}>
                {group.items.map(tab => (
                  <li key={tab.key}>
                    <NavLink
                      to={tab.path}
                      onClick={(e) => handleNavClick(tab, e)}
                      className={({ isActive }) =>
                        `group flex items-center transition-all duration-150 relative font-regular ${
                          isActive || location.pathname === tab.path
                            ? 'bg-stone-950 text-white shadow-sm'
                            : 'text-stone-800 hover:bg-stone-50 hover:text-app-primary'
                        } ${
                          isCollapsed 
                            ? 'justify-center w-14 h-14 rounded-xl mx-auto' // Perfect square for icon centering
                            : 'gap-3 px-3 py-2.5 rounded-xl min-h-[44px]'
                        }`
                      }
                    >
                      <span className={`flex items-center justify-center ${
                        isCollapsed ? 'w-5 h-5' : 'flex-shrink-0'
                      }`}>
                        {tab.icon}
                      </span>
                      <span 
                        className={`transition-all duration-200 text-sm ${
                          isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 flex-1'
                        }`}
                      >
                        {tab.label}
                      </span>
                      
                      {/* Beta indicator */}
                      {tab.isBeta && !isCollapsed && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full transition-all duration-200">
                          Beta
                        </span>
                      )}
                      
                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-3 px-2 py-1 bg-slate-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-70">
                          <div className="flex items-center gap-2">
                            {tab.label}
                            {tab.isBeta && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-500 text-white rounded-full">
                                Beta
                              </span>
                            )}
                          </div>
                          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900"></div>
                        </div>
                      )}

                      {/* Dynamic notification badges */}
                      {tab.key === 'reminders' && renderBadge('reminders', badges.messages, 'bg-magenta-500')}
                      {tab.key === 'packing' && renderBadge('packing', badges.packing, 'bg-indigo-500')}
                      {tab.key === 'shopping' && renderBadge('shopping', badges.shopping, 'bg-coral-500')}
                      {tab.key === 'family' && renderBadge('family', badges.events, 'bg-gold-500')}
                      {tab.key === 'tasks' && renderBadge('tasks', badges.tasks, 'bg-azure-500')}
                      {tab.key === 'income-hub' && renderBadge('income-hub', badges.allowance, 'bg-teal-500')}
                      {tab.key === 'college-journey' && renderBadge('college-journey', badges.college, 'bg-violet-500')}
                      {tab.key === 'recs-ideas' && renderBadge('recs-ideas', badges.recommendations, 'bg-indigo-500')}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-app-subtle">



          {/* Compact View Switcher - Only available for parents */}
          {familyUsers && familyUsers.length > 1 && (currentUser?.role === 'parent' || currentUser?.type === 'parent' || currentUser?.isParent || currentUser?.originalParent) && (
            <div className="mb-3">
              <CompactViewSwitcher 
                currentUser={currentUser}
                familyUsers={familyUsers}
                switchUser={switchUser}
                isCollapsed={isCollapsed}
              />
            </div>
          )}

          {/* User Profile */}
          <div className={`${isCollapsed ? 'flex justify-center' : ''}`}>
            <div 
              onClick={() => navigate('/account-settings')}
              className={`flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer ${
                isCollapsed ? 'justify-center px-2' : ''
              }`}
            >
              <div className="w-9 h-9 bg-slate-900 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {getUserInitials()}
              </div>
              <div className={`transition-all duration-200 ${
                isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
              }`}>
                <div className="font-semibold text-sm text-app-primary">
                  {currentUser?.name || 'User'}
                </div>
                <div className="text-xs text-app-subtle">
                  {isParent ? 'Parent' : 'Kid'} • Account Settings
                  {isParent && (
                    <span 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/admin');
                      }}
                      className="ml-2 text-app-primary hover:text-app-primary-dark cursor-pointer font-medium"
                    >
                      • Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contextual Expand Button */}
        {isCollapsed && screenSize !== 'mobile' && (
          <button
            onClick={toggleSidebar}
            className="absolute -right-3 top-6 w-6 h-6 bg-white border border-app-subtle rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 text-app-tertiary hover:text-app-primary"
            title="Expand sidebar (⌘B)"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </aside>

      {/* Keyboard Hint */}
      {screenSize !== 'mobile' && (
        <div className={`fixed bottom-5 right-5 bg-slate-900 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all duration-300 pointer-events-none z-70 ${
          showKeyboardHint ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
          Press ⌘B to toggle sidebar
        </div>
      )}
    </>
  );
}